import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import Search from './pages/Search';
import ListingDetail from './pages/ListingDetail';
import DirectoryPage from './pages/DirectoryPage';
import Pricing from './pages/Pricing';
import AddListing from './pages/AddListing';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import CityPage from './pages/CityPage';
import SpecialtyPage from './pages/SpecialtyPage';
import UpgradeSuccess from './pages/UpgradeSuccess';
import SEOManager from './pages/SEOManager';
import AccountSettings from './pages/AccountSettings';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router Component - handles session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST before checking existing session_token
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Search & Directory */}
      <Route path="/search" element={<Search />} />
      <Route path="/facilities" element={<DirectoryPage type="facilities" />} />
      <Route path="/specialists" element={<DirectoryPage type="specialists" />} />
      <Route path="/pharmacies" element={<DirectoryPage type="pharmacies" />} />
      
      {/* Listing Detail */}
      <Route path="/listing/:id" element={<ListingDetail />} />
      
      {/* SEO Pages */}
      <Route path="/city/:slug" element={<CityPage />} />
      <Route path="/specialty/:slug" element={<SpecialtyPage />} />
      
      {/* Pricing */}
      <Route path="/pricing" element={<Pricing />} />
      
      {/* Add Listing - can be public or protected based on your needs */}
      <Route path="/add-listing" element={<AddListing />} />
      
      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/seo"
        element={
          <ProtectedRoute>
            <SEOManager />
          </ProtectedRoute>
        }
      />
      
      {/* Payment Success */}
      <Route path="/upgrade/success" element={<UpgradeSuccess />} />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main Layout with Header/Footer
const MainLayout = ({ children }) => {
  const location = useLocation();
  
  // Hide header/footer on auth pages
  const isAuthPage = ['/login', '/signup', '/auth/callback'].includes(location.pathname) ||
    location.hash?.includes('session_id=');
  
  if (isAuthPage) {
    return <>{children}</>;
  }
  
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <MainLayout>
          <AppRouter />
        </MainLayout>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
